import { useTaskWithErrorHandling } from "@/hooks/useTaskWithErrorHandling";
import { submitPublicRequest } from "@/Api/PublicRequestApi";
import { PublicRequestConfirmModal } from "@/Components/PublicRequest/PublicRequestConfirmModal";
import { MessageAlert } from "@/Components/MessageAlert";
import Section from "@/Components/MagicLink/Section";
import { usePublicRequestProvider } from "@/Provider/PublicRequestProvider";
import { buildPublicSearchPath } from "@/Router/publicRoutes";
import { InsuranceCategoryEnum } from "@/Schemas/PublicRequest/InsuranceCategoryEnum";
import { PublicRequestValidateOutput } from "@/Schemas/PublicRequest/PublicRequestValidateOutput";
import {
    magicLinkPageColumnSx,
    publicRequestContentSx,
} from "@/Utils/MagicLink/pageLayout";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { LoadingButton } from "@mui/lab";
import { Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { useState, type ComponentType } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowRightIcon,
    CartAltIcon,
    HeartHandshakeIcon,
    HomeHeartIcon,
    PersonLuggageIcon,
    TrashIcon,
} from "request-form-lib";
import { RequestMasterTypeMessage } from "@/Config/RequestMasterTypeConfig";

function DetailView() {
    const theme = useTheme();
    const navigate = useNavigate();
    const {
        contractData,
        getRecaptchaCode,
        searchRequestMasterType,
        setPublicRequestResult,
        setTokenStep,
        masterTypeConfig
    } = usePublicRequestProvider();

    const actionTitle = masterTypeConfig!.actionTitle;
    const reviewTitle = masterTypeConfig!.reviewTitle;
    const warningMessage = masterTypeConfig!.reviewWarning;

    const isFromSearch = searchRequestMasterType !== null;

    const handleBackToSearch = () => {
        if (searchRequestMasterType) {
            navigate(buildPublicSearchPath(searchRequestMasterType.valueOf()));
        }
    };

    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

    const [hasSubmitError, setHasSubmitError] = useState(false);

    const [contractErrorOccured, setContractErrorOccured] = useState(() => false);
    const [genericErrorMessage, setGenericErrorMessage] = useState(() => "");

    const notFoundMessage = masterTypeConfig?.invalidContractOnDetailView;

    const submitErrorAlertText = contractErrorOccured && notFoundMessage ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="subtitle1" component="div" fontWeight={600}>
                {notFoundMessage.headline}
            </Typography>
            <Typography variant="body2" component="div">
                {notFoundMessage.description}
            </Typography>
        </Box>
    ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="subtitle1" component="div" fontWeight={600}>
                {genericErrorMessage}
            </Typography>
        </Box>
    );

    const submitRequestTask = useTaskWithErrorHandling({
        asyncFunction: async () => {
            setHasSubmitError(false);
            const recaptchaResponse = await getRecaptchaCode();
            const data = await submitPublicRequest({
                token: contractData?.token ?? "",
                recaptcha: recaptchaResponse
            });

            setPublicRequestResult(data);
            setTokenStep("success");
        },
        onError: (message) => {
            setPublicRequestResult(null);
            setContractErrorOccured(message === "PUBLIC_REQUEST_INVALID")
            setGenericErrorMessage(message !== "PUBLIC_REQUEST_INVALID"
                ? message
                : "Zmluva sa nenašla alebo požadovaný úkon nie je dostupný. Skúste to prosím neskôr."
            )
            setHasSubmitError(true);
        },
        genericErrorMessage: "Nastala chyba počas spracovania požiadavky. Skúste to prosím neskôr.",
    });

    const handleOpenCancelModal = () => {
        setIsCancelModalOpen(true);
    };

    const handleCloseCancelModal = () => {
        if (submitRequestTask.isRunning) {
            return;
        }
        setIsCancelModalOpen(false);
    };

    const handleConfirmCancelContract = () => {
        setIsCancelModalOpen(false);
        void submitRequestTask.perform();
    };

    if (!contractData) {
        return null;
    }

    const isProcessing = submitRequestTask.isRunning;

    const renderBackButton = (forDesktop: boolean) => (
        <Button
            type="button"
            variant={forDesktop ? "text" : "outlined"}
            color={forDesktop ? "primary" : undefined}
            disabled={isProcessing}
            onClick={handleBackToSearch}
            startIcon={
                <Box
                    component="span"
                    sx={{
                        display: "inline-flex",
                        lineHeight: 0,
                        transform: "rotate(180deg)",
                    }}
                >
                    <ArrowRightIcon
                        width={16}
                        height={16}
                        color={forDesktop ? theme.palette.primary.main : "#000"}
                    />
                </Box>
            }
            sx={forDesktop ? detailViewDesktopBackButtonSx : detailViewMobileBackButtonSx}
        >
            Krok späť
        </Button>
    );

    const renderCancelContractButton = () => (
        <LoadingButton
            type="button"
            variant="contained"
            color="primary"
            loading={isProcessing}
            loadingPosition="center"
            loadingIndicator={<CircularProgress size={22} color="inherit" />}
            onClick={handleOpenCancelModal}
            startIcon={
                !isProcessing ? (
                    <TrashIcon
                        width={18}
                        height={18}
                        color={theme.palette.primary.contrastText}
                    />
                ) : undefined
            }
            sx={{
                ...detailViewCancelButtonSx,
                minWidth: { md: isProcessing ? 160 : undefined },
            }}
        >
            {!isProcessing && actionTitle}
        </LoadingButton>
    );

    return (
        <Box component="main" sx={detailViewContentSx}>
            <Box
                sx={{
                    ...magicLinkPageColumnSx,
                    maxWidth: { xs: "100%", md: "682px" },
                    minWidth: { xs: 0, md: 682 },
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "32px",
                }}
            >

                {hasSubmitError && submitErrorAlertText && (
                    <Box sx={{ display: { xs: "block", md: "none" }, width: "100%" }}>
                        <MessageAlert
                            severity="error"
                            text={submitErrorAlertText}
                            sx={{ marginTop: 0 }}
                        />
                    </Box>
                )}

                <Section
                    icon={
                        <VisibilityOutlinedIcon
                            sx={{
                                width: 28,
                                height: 28,
                                color: theme.palette.primary.main,
                            }}
                        />
                    }
                    title={reviewTitle}
                    sx={{
                        minWidth: { xs: 0, md: 682 },
                        maxWidth: "100%",
                        width: "100%",
                        boxSizing: "border-box",
                    }}
                    disableBodyPadding
                    bodySx={{ gap: 0 }}
                >
                    <ProductInfoRow contractData={contractData} />

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "24px",
                            width: "100%",
                            padding: "24px",
                            boxSizing: "border-box",
                        }}
                    >

                        <MessageAlert
                            severity="warning"
                            text={createWarningAlertText(warningMessage)}
                            sx={{ marginTop: 0 }}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "16px",
                                width: "100%",
                            }}
                        >
                            {contractData.fields
                                .filter(field => field.value)
                                .map((field, index) => (
                                <Box
                                    key={`${field.key}-${index}`}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "flex-start",
                                        justifyContent: "space-between",
                                        gap: 2,
                                        width: "100%",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ flex: 1, minWidth: 0 }}
                                    >
                                        {field.key}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 700,
                                            color: "#000",
                                            flexShrink: 0,
                                            textAlign: "right",
                                            maxWidth: "60%",
                                            "word-break": "break-word"
                                        }}
                                    >
                                        {field.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {contractData.flags.hasIban && (
                            <MessageAlert
                                severity="info"
                                text="Nespotrebované poistné bude zaslané na IBAN, z ktorého bola vykonaná úhrada."
                                sx={{ marginTop: 0 }}
                            />
                        )}

                        {!contractData.flags.hasIban && (
                            <MessageAlert
                                severity="info"
                                text="Nespotrebované poistné bude zaslané šekovou poukážkou na trvalú adresu poistníka."
                                sx={{ marginTop: 0 }}
                            />
                        )}
                    </Box>
                </Section>

                {hasSubmitError && submitErrorAlertText && (
                    <Box sx={{ display: { xs: "none", md: "block" }, width: "100%" }}>
                        <MessageAlert
                            severity="error"
                            text={submitErrorAlertText}
                            sx={{ marginTop: 0 }}
                        />
                    </Box>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                    <Box
                        sx={{
                            display: { xs: "none", md: "flex" },
                            alignItems: "center",
                            justifyContent:
                                isFromSearch
                                    ? "space-between"
                                    : "flex-end",
                            width: "100%",
                            gap: 2,
                        }}
                    >
                        {isFromSearch && renderBackButton(true)}
                        {renderCancelContractButton()}
                    </Box>

                    <Box
                        aria-hidden
                        sx={{
                            display: { xs: "block", md: "none" },
                            flexShrink: 0,
                            height: `${MOBILE_CTA_FOOTER_HEIGHT_PX}px`,
                        }}
                    />

                    <Box sx={detailViewMobileFooterSx(theme)}>
                        {isFromSearch && renderBackButton(false)}
                        {renderCancelContractButton()}
                    </Box>
                </Box>
            </Box>

            <PublicRequestConfirmModal
                open={isCancelModalOpen}
                onClose={handleCloseCancelModal}
                onConfirm={handleConfirmCancelContract}
                isConfirming={submitRequestTask.isRunning}
                masterTypeConfig={masterTypeConfig}
            />
        </Box>
    );
}

export default DetailView;

const MOBILE_CTA_FOOTER_HEIGHT_PX = 88;

const detailViewMobileFooterSx = (theme: Theme): SxProps<Theme> => ({
    display: { xs: "flex", md: "none" },
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1100,
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingX: "16px",
    paddingTop: 2,
    paddingBottom: "max(16px, env(safe-area-inset-bottom))",
    alignItems: "center",
    gap: "10px",
    width: "100%",
});

const detailViewMobileBackButtonSx: SxProps<Theme> = {
    flex: 1,
    minWidth: 0,
    height: 48,
    minHeight: 48,
    textTransform: "none",
    fontWeight: 500,
    color: "#000",
    borderColor: (theme) => theme.palette.divider,
    "&:hover": {
        borderColor: "#000",
        backgroundColor: (theme) => theme.palette.action.hover,
    },
};

const detailViewDesktopBackButtonSx: SxProps<Theme> = {
    flex: "0 0 auto",
    height: 48,
    minHeight: 48,
    textTransform: "none",
    fontWeight: 500,
};

const detailViewCancelButtonSx: SxProps<Theme> = {
    flex: { xs: 1, md: "0 0 auto" },
    minWidth: { xs: 0, md: 160 },
    height: 48,
    minHeight: 48,
    textTransform: "none",
    width: { xs: "auto", md: "fit-content" },
};

const detailViewContentSx: SxProps<Theme> = {
    ...publicRequestContentSx,
    paddingX: { xs: "16px", md: 2 },
    paddingY: { xs: "24px", md: "80px" },
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "hidden",
};

const createWarningAlertText = (
    warningMessage: RequestMasterTypeMessage
) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography variant="subtitle1" component="div" fontWeight={600}>
            {warningMessage.headline}
        </Typography>
        <Typography
            variant="body2"
            component="div"
            color="text.primary"
            dangerouslySetInnerHTML={{
                __html: warningMessage.description,
            }}
        />
    </Box>
);

type ProductInfoConfig = {
    backgroundColor: string;
    headline: string;
    Icon: ComponentType<{ width?: number; height?: number; color?: string }>;
};

const PRODUCT_INFO_BY_TYPE: Record<InsuranceCategoryEnum, ProductInfoConfig> = {
    [InsuranceCategoryEnum.PROPERTY_INSURANCE]: {
        backgroundColor: "#6F56A0",
        headline: "Poistenie majetku",
        Icon: HomeHeartIcon,
    },
    [InsuranceCategoryEnum.VEHICLE_INSURANCE]: {
        backgroundColor: "#88A926",
        headline: "Poistenie vozidla",
        Icon: CartAltIcon,
    },
    [InsuranceCategoryEnum.TRAVEL_INSURANCE]: {
        backgroundColor: "#00A9AE",
        headline: "Cestovné poistenie",
        Icon: PersonLuggageIcon,
    },
    [InsuranceCategoryEnum.LIFE_INSURANCE]: {
        backgroundColor: "#C40109",
        headline: "Životné poistenie",
        Icon: HeartHandshakeIcon,
    },
};

const juliFontFeatureSettings = { fontFeatureSettings: "'liga' off, 'clig' off" } as const;

const productInfoHeadlineSx: SxProps<Theme> = {
    color: "#232730",
    ...juliFontFeatureSettings,
    fontFamily: '"Juli Sans", Juli, sans-serif',
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "148%",
};

const productInfoProductNameSx: SxProps<Theme> = {
    color: "#333",
    ...juliFontFeatureSettings,
    fontFamily: '"Juli Sans", Juli, sans-serif',
    fontSize: "13px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "132%",
};

type ProductInfoRowProps = {
    contractData: PublicRequestValidateOutput;
};

function ProductInfoRow({ contractData }: ProductInfoRowProps) {
    const theme = useTheme();
    const { backgroundColor, headline, Icon } =
        PRODUCT_INFO_BY_TYPE[contractData.insuranceCategory];

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 2,
                width: "100%",
                margin: 0,
                padding: "24px",
                borderBottom: `1px solid ${theme.palette.divider}`,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    width: 48,
                    height: 48,
                    flexShrink: 0,
                    borderRadius: "7px",
                    backgroundColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Icon width={24} height={24} color="#fff" />
            </Box>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    gap: 0,
                    minWidth: 0,
                }}
            >
                <Typography component="p" sx={productInfoHeadlineSx}>
                    {headline}
                </Typography>
                {contractData.productName && (
                    <Typography component="p" sx={productInfoProductNameSx}>
                        {contractData.productName}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
